import { useEffect } from 'react'
import { createFileRoute, Outlet } from '@tanstack/react-router'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty } from 'lodash'
import {
  Loader,
  NoBgLoaderWrapper
} from '../../../../components/page-loader/styled-components'
import {
  clearPlans,
  getRootPlans
} from '../../../../containers/PlanSelection/actions'
import { makeSelectRootPlans } from '../../../../containers/PlanSelection/selectors'
import { makeSelectGetPlanRevisionError } from '../../../../containers/plan-map/selectors'
import {
  clearProjectDetails,
  getProjectDetails
} from '../../../../containers/ProjectSettings/actions'
import {
  makeSelectGetProjectDetailsError,
  makeSelectProjectDetails
} from '../../../../containers/ProjectSettings/selectors'
import { useGetProjectContactsQuery } from '../../../../react-query/members/query/use-get-project-contacts-query'
import { useGetProjectMembersQuery } from '../../../../react-query/members/query/use-get-project-members-query'
import { useGetProjectTeamsQuery } from '../../../../react-query/members/query/use-get-project-teams-query'

export const Route = createFileRoute('/_authenticated/project/$projectId')({
  component: ProjectBoundary
})

function ProjectBoundary() {
  const { projectId } = Route.useParams()
  const navigate = Route.useNavigate()
  const dispatch = useDispatch()

  const plans = useSelector(makeSelectRootPlans())
  const projectDetails = useSelector(makeSelectProjectDetails())
  const projectDetailsError = useSelector(makeSelectGetProjectDetailsError())
  const planRevisionError = useSelector(makeSelectGetPlanRevisionError())

  // projectId comes straight from the route param, so it can be a
  // non-project value (e.g. a stale "/project/upgrade" link). Only
  // pre-fetch project-scoped data once the project itself is confirmed.
  const isConfirmedProject = !!projectDetails && projectDetails.id === projectId

  // pre-fetch assignees for tasks
  useGetProjectMembersQuery(
    projectId,
    '',
    { size: 100 },
    { enabled: isConfirmedProject }
  )
  useGetProjectContactsQuery(projectId, '', { enabled: isConfirmedProject })
  useGetProjectTeamsQuery(projectId, '', { enabled: isConfirmedProject })

  useEffect(() => {
    if (!projectId) {
      return
    }

    if (isEmpty(plans) || !projectDetails || projectDetails.id !== projectId) {
      dispatch(clearPlans())
      dispatch(getRootPlans(projectId, 0, 'Title', 'Active', ''))

      if (!projectDetails || projectDetails.id !== projectId) {
        dispatch(clearProjectDetails())
        dispatch(getProjectDetails(projectId))
      }
    }
  }, [dispatch, plans, projectDetails, projectId])

  useEffect(() => {
    if (
      (!!projectDetailsError && projectDetailsError.status === 403) ||
      planRevisionError?.status === 403
    ) {
      navigate({ to: '/project' })
    }
  }, [navigate, planRevisionError, projectDetailsError])

  if (!projectDetails || projectDetails.id !== projectId) {
    return (
      <NoBgLoaderWrapper>
        <Loader className="loader" />
      </NoBgLoaderWrapper>
    )
  }

  return <Outlet />
}
